import { ChangeEvent, useState, useRef } from 'react';
import clsx from 'clsx';
import { Button } from '@nexelem/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faFileArrowDown } from '@fortawesome/pro-light-svg-icons';
import { InputLabel } from '../InputLabel';
import { InternalFieldProps } from '../Internal';

export type UploadInputProps = Pick<InternalFieldProps, 'size' | 'error' | 'errorMessage' | 'required'> & {
  label?: string;
  onChange: (value: File) => void;
  placeholder?: string;
  fileName?: string;
  size?: 'medium' | 'large' | 'jumbo';
};

export const UploadInput = ({
  onChange,
  placeholder,
  error,
  errorMessage,
  required,
  label,
  fileName: fileNameProps = '',
  size = 'medium',
}: UploadInputProps) => {
  const [fileName, setFileName] = useState(fileNameProps);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event?.target?.files?.[0];
    setFileName(file?.name ?? '');
    if (file) {
      onChange(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <InputLabel className="pb-2 pl-2" label={label} required={required} />
      <Button
        fullWidth
        EndIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
        size={size}
        variant="outlined"
        className={clsx(
          'justify-between border-border-primary hover:bg-transparent active:bg-transparent',
          error &&
            'border-border-state-error-dark hover:border-border-state-error-dark active:border-border-state-error-dark',
        )}
        onClick={handleButtonClick}
      >
        <span
          className={clsx(
            'truncate text-s18 font-normal tracking-normal',
            fileName ? 'text-text-primary' : 'text-text-secondary italic',
          )}
        >
          {fileName || placeholder}
        </span>
      </Button>
      <input hidden ref={fileInputRef} type="file" onChange={handleChange} />
      {error && (
        <div className="flex gap-2 pt-2 pl-2 text-icon-error">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span className="text-t14-sb">{errorMessage}</span>
        </div>
      )}
    </>
  );
};
